import React, { useState, useEffect, useRef } from "react";
import { FaPlus, FaSearch, FaBuilding, FaTag, FaCalendar, FaEye, FaTimes, FaUsers, FaMoneyBillWave, FaUser, FaWallet, FaEdit, FaCheck, FaBan, FaExclamationTriangle } from "react-icons/fa";
import { FiCloudLightning, FiEye } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";
import {
  getSingleCampaign,
  getSingleCampaignRules,
  postSingleCampaignRules,
  getCampaignRequest,
  updateCampaign,
  updateRules,
  postCampaign,
  updateCampaignRequest as UpdateRequest
} from "../../services/Campaigns.ts";
import CampaignMenu from "../../components/CampaignMenu.tsx";
import InfluencerModal from "../../components/Influencer.tsx";
import Tasks from "../../components/Tasks.tsx";
import Toast from "../../components/common/Toast.tsx";
import { createOrderLink } from "../../services/Payments.ts";
import { useCampaigns, useDeleteCampaign } from "../../hooks/useCampaigns.ts";
import { useUserBrands } from "../../hooks/useBrands.ts";
import { useProfile } from "../../hooks/useProfile.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from '../../theme.ts';
import { showConfirmation } from "../../services/confirmation.ts";
import toast from 'react-hot-toast';
import RulesModal from "../../components/RulesModal.tsx";
import { useLoading } from "../../layouts/DashboardLayout.tsx";

interface Campaign {
  id: number;
  title: string;
  brand: string;
  type: string;
  deadline: string;
  bannerImage?: string;
  campaign_budget: string;
  individual_budget: string;
  budget_left?: string;
  platforms: string[];
  overview: string;
  banner: string;
  brand_name: string;
  is_live: boolean;
  brand_title: string;
}

interface Brand {
  id: number;
  title: string;
  description: string;
  logo: string;
  location: string;
}

interface ApiResponse<T> {
  status: number;
  data: T;
}

interface CampaignData {
  campaign: Campaign;
}

interface RulesData {
  dos: string[] | string;
  donts: string[] | string;
  [key: string]: any;
}

const Campaigns = () => {
  const { setIsLoading } = useLoading();
  const { themeName } = useTheme();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // React Query Hooks
  const { data: campaignsData, isLoading: isLoadingCampaigns, refetch: refetchCampaigns } = useCampaigns(cred?.user?.id);
  const { data: brandsData, isLoading: isLoadingBrands } = useUserBrands(cred?.user?.id);
  const deleteCampaignMutation = useDeleteCampaign();

  // Sync React Query data with local state for filtering & brands
  useEffect(() => {
    if (campaignsData) {
      // Handle both direct array and nested data structure
      const data = Array.isArray(campaignsData)
        ? campaignsData
        : (campaignsData as any)?.data || [];

      setCampaigns(data);
      setFilteredCampaigns(data);
    }
  }, [campaignsData]);

  useEffect(() => {
    if (brandsData) {
      setBrands(brandsData);
    }
  }, [brandsData]);

  const [campaignName, setCampaignName] = useState("");
  const [campaingOverview, setCampaingOverview] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [type, setType] = useState("");
  const [deadline, setDeadline] = useState("");
  const [bannerImage, setBannerImage] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [campaignBudget, setCampaignBudget] = useState("");
  const [individualBudget, setIndividualBudget] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [platforms, setPlatforms] = useState<string[]>([]);

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [rules, setRules] = useState<any>(null);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedCampaignForRequests, setSelectedCampaignForRequests] = useState<Campaign | null>(null);
  const [isStatusPopupOpen, setIsStatusPopupOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showInfluencer, setShowInfluencer] = useState(false);

  // Rules edit states
  const [isEditRulesMode, setIsEditRulesMode] = useState(false);
  const [editingRules, setEditingRules] = useState<any>(null);

  // Delete confirmation state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);

  // Toast states
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error" | "info">("info");
  const [isToastVisible, setIsToastVisible] = useState(false);

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [isProfileVerified, setIsProfileVerified] = useState(false);
  const [profileCheckComplete, setProfileCheckComplete] = useState(false);
  const { data: profileData, isLoading: isLoadingProfile } = useProfile(cred?.user?.id);

  const handleSearch = () => {
    let filtered = campaigns.filter(
      (c) =>
        c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.brand.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (filterDate) {
      filtered = filtered.filter((c) => c.deadline === filterDate);
    }

    setFilteredCampaigns(filtered);
  };

  // getUserCamp replaced by useCampaigns hook

  // loadBrands replaced by useUserBrands hook

  // Form validation function
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!campaignName.trim()) newErrors.campaignName = "Campaign title is required";
    if (!campaingOverview.trim()) newErrors.campaingOverview = "Overview is required";
    if (!selectedBrandId) newErrors.selectedBrandId = "Please select a brand";
    if (!type) newErrors.type = "Campaign type is required";
    if (!deadline) newErrors.deadline = "Deadline is required";

    if (isNaN(Number(campaignBudget))) newErrors.campaignBudget = "Budget must be a number";
    if (Number(campaignBudget) <= 0) newErrors.campaignBudget = "Budget must be positive";

    if (isNaN(Number(individualBudget))) newErrors.individualBudget = "Budget must be a number";
    if (Number(individualBudget) <= 0) newErrors.individualBudget = "Budget must be positive";

    if (platforms.length === 0) newErrors.platforms = "At least one platform is required";

    // Remove banner image requirement for edit mode
    if (!bannerImage && !editingCampaign) newErrors.bannerImage = "Banner image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (campaignName && selectedBrandId && type && deadline) {
      const formData = new FormData();
      formData.append("title", campaignName);
      formData.append("overview", campaingOverview);

      // Handle banner image - only append if there's a new one
      if (bannerImage) {
        formData.append("banner", bannerImage as Blob);
      }
      // For editing, if no new image is selected, the existing one will be preserved automatically

      formData.append("type", type);
      formData.append("deadline", deadline);
      formData.append("brand", selectedBrandId);
      formData.append("campaign_budget", campaignBudget);
      formData.append("individual_budget", individualBudget);
      formData.append("platforms", JSON.stringify(platforms));

      try {
        setProgress(0);
        const interval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              clearInterval(interval);
              return prev;
            }
            return prev + 10;
          });
        }, 200);

        let response;
        if (editingCampaign) {
          response = await updateCampaign(editingCampaign.id, formData);
          toast.success("Campaign updated successfully!");
        } else {
          response = await postCampaign(formData);
          toast.success("Campaign created successfully!");
        }

        setProgress(100);
        setTimeout(() => setProgress(0), 500);

        if (response?.status === 403) {
          setToastMessage("Profile not verified. Please complete your profile verification.");
          setToastType("error");
          setIsToastVisible(true);
        } else {
          setToastMessage(editingCampaign ? "Campaign updated successfully!" : "Campaign created successfully!");
          setToastType("success");
          setIsToastVisible(true);
          setIsToastVisible(true);
          refetchCampaigns();
        }
      } catch (err) {
        // FIXED: Proper error handling with toasts
        if ((err as any)?.response?.status === 403) {
          setToastMessage("Profile not verified. Please complete your profile verification.");
          setToastType("error");
          setIsToastVisible(true);
        } else {
          console.error("Error creating/updating campaign:", err);
          setToastMessage("An error occurred. Please try again.");
          setToastType("error");
          setIsToastVisible(true);
        }
        setProgress(0);
      }

      setIsModalOpen(false);
      setEditingCampaign(null);
      resetForm();
    }
  };

  const resetForm = () => {
    setCampaignName("");
    setCampaingOverview("");
    setSelectedBrandId("");
    setType("");
    setDeadline("");
    setBannerImage(null);
    setBannerPreview(null);
    setCampaignBudget("");
    setIndividualBudget("");
    setPlatforms([]);
    setErrors({});
  };

  const handleDeleteClick = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (campaignToDelete) {
      try {
        await deleteCampaignMutation.mutateAsync(campaignToDelete.id);
        setIsDeleteModalOpen(false);
        setCampaignToDelete(null);
        // Toast handled by mutation hook
      } catch (err) {
        // Error handled by mutation hook
        console.log("Delete failed", err);
      }
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setCampaignToDelete(null);
  };

  const handleViewCampaign = async (id: number) => {
    try {
      const response = await getSingleCampaign(token, id);
      setSelectedCampaign(response?.data?.data?.campaign);
      setIsDetailModalOpen(true);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load campaign details");
    }
  };

  // Function to open edit modal with campaign data
  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setCampaignName(campaign.title);
    setCampaingOverview(campaign.overview || "");
    setSelectedBrandId(campaign.brand);
    setType(campaign.type);

    // Format date for input field (YYYY-MM-DD)
    const deadlineDate = new Date(campaign.deadline);
    const formattedDate = deadlineDate.toISOString().split('T')[0];
    setDeadline(formattedDate);

    setCampaignBudget(campaign.campaign_budget);
    setIndividualBudget(campaign.individual_budget);
    setPlatforms(
      campaign.platforms
        ? (typeof campaign.platforms === 'string'
          ? JSON.parse(campaign.platforms)
          : campaign.platforms as string[])
        : []
    );
    setBannerPreview(campaign.banner || null);
    setIsModalOpen(true);
  };

  const loadRules = async (id: number) => {
    try {
      const response = await getSingleCampaignRules(id);
      const rulesData = (response as any)?.data?.[0];
      if (rulesData) {
        // Convert rules to array format if they're strings
        const dos = typeof rulesData.dos === 'string'
          ? rulesData.dos.split(',').map((s: string) => s.trim()).filter((s: string) => s)
          : rulesData.dos || [];

        const donts = typeof rulesData.donts === 'string'
          ? rulesData.donts.split(',').map((s: string) => s.trim()).filter((s: string) => s)
          : rulesData.donts || [];

        setRules({ ...rulesData, dos, donts });
      } else {
        setRules(null);
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to load campaign rules");
    }
  };

  const loadUserType = () => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserType(parsedUser?.user?.role);
      } catch (error) {
        console.error("Error parsing user data:", error);
        setUserType(null);
      }
    } else {
      setUserType(null);
    }
  };

  // Rules edit functions
  const handleEditRules = (rules: any) => {
    setEditingRules(rules);
    setIsEditRulesMode(true);
    setIsRuleModalOpen(true);
  };

  const handleUpdateRules = async (id: string, dos: string[], donts: string[]) => {
    if (selectedCampaign) {
      const formData = new FormData();
      formData.append('dos', JSON.stringify(dos));
      formData.append('donts', JSON.stringify(donts));
      formData.append('campaign', selectedCampaign.id.toString());

      try {
        const response = await updateRules(id, formData);
        if (response && response.status === 200) {
          toast.success("Rules updated successfully!");
          loadRules(selectedCampaign.id);
          setIsRuleModalOpen(false);
          setIsEditRulesMode(false);
          setEditingRules(null);
          setToastMessage("Rules updated successfully!");
          setToastType("success");
          setIsToastVisible(true);
        }
      } catch (err) {
        console.log(err);
        setToastMessage("An error occurred while updating rules. Please try again.");
        setToastType("error");
        setIsToastVisible(true);
      }
    }
  };

  const handleCancelEditRules = () => {
    setIsRuleModalOpen(false);
    setIsEditRulesMode(false);
    setEditingRules(null);
  };

  const loadRequests = async (id: number) => {
    try {
      setLoadingRequests(true);
      const response: any = await getCampaignRequest(token, id);
      if (response?.status === 200) {
        setRequests(response.data);
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to load requests");
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleShowRequests = (campaign: Campaign) => {
    setSelectedCampaignForRequests(campaign);
    setIsRequestModalOpen(true);
    loadRequests(campaign.id);
  };

  const handleShowInfluencers = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowInfluencer(true);
  };

  const handleStatusClick = (request: any) => {
    setSelectedRequest(request);
    setIsStatusPopupOpen(true);
  };

  const updateRequestStatus = async (request: any, status: string) => {
    const data = new FormData();
    data.append('status', status)
    data.append('campaign', request?.campaign)
    data.append('user', request?.user)
    data.append('id', request?.id)

    try {
      const response = await UpdateRequest(token, data, request?.id);
      toast.success(`Request ${status.toLowerCase()} successfully!`);
      setIsStatusPopupOpen(false);
      loadRequests(selectedCampaignForRequests?.id || 0);
    } catch (err) {
      console.error("Failed to update request status:", err);
      toast.error("Failed to update request status");
    }
  };

  const handleCreateOrder = async (campaign: Campaign) => {
    if (!campaign.campaign_budget || isNaN(Number(campaign.campaign_budget))) {
      toast.error("Invalid campaign budget");
      return;
    }

    try {
      setIsLoading(true);
      const response = await createOrderLink(token, {
        order_type: "CAMPAIGN",
        amount: Number(campaign.campaign_budget),
        campaign: campaign.id
      });

      if (response.data?.success && response.data?.payment_url) {
        const newWindow = window.open(response.data.payment_url, '_blank');
        if (newWindow) {
          newWindow.focus();
          toast.success("Payment page opened", { duration: 5000 });
        } else {
          toast.error("Popup blocked! Please allow popups.", { duration: 5000 });
          // Fallback: try direct navigation if popup fails (optional, but requested behavior is new tab)
          // window.location.href = response.data.payment_url; 
        }
      } else {
        toast.error("Failed to generate payment link");
      }
    } catch (err) {
      console.error("Error creating order:", err);
      const errorMessage = (err as any)?.response?.data?.message || "Failed to create order";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserType();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      loadRules(selectedCampaign.id);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    if (isRequestModalOpen && selectedCampaignForRequests) {
      loadRequests(selectedCampaignForRequests.id);
    }
  }, [isRequestModalOpen, selectedCampaignForRequests]);

  const navigate = useNavigate();

  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target as Node)
      ) {
        setTypeDropdownOpen(false);
      }
    }
    if (typeDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [typeDropdownOpen]);

  const availablePlatforms = [
    "YouTube", "Instagram", "Facebook",
    "LinkedIn", "X (Twitter)"
  ];

  useEffect(() => {
    if (profileData) {
      const isVerified = (profileData as any)?.user?.profile?.is_verified || false;
      setIsProfileVerified(isVerified);
      setProfileCheckComplete(true);
    } else if (!isLoadingProfile) {
      setProfileCheckComplete(true);
    }
  }, [profileData, isLoadingProfile]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: themes[themeName].colors.background, color: themes[themeName].colors.text }}>
      {/* Filters and Add Button Row */}
      <div className="flex items-center justify-between mb-6 flex-wrap">
        <div className="flex flex-wrap items-center gap-4">
          <input
            type="text"
            placeholder="Search by name or brand"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border p-2 rounded w-64 focus:ring-2 focus:ring-primary"
            style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }}
          />
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="border p-2 rounded focus:ring-2 focus:ring-primary"
            style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: themes[themeName].colors.border }}
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 rounded flex items-center gap-2"
            style={{ backgroundColor: themes[themeName].colors.primary, color: themes[themeName].colors.surface }}
          >
            <FaSearch /> <span className='hidden md:inline-block'>Search</span>
          </button>
        </div>
        {profileCheckComplete && (
          <>
            {isProfileVerified ? (
              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingCampaign(null);
                  resetForm();
                }}
                className="px-4 py-2 rounded shadow flex items-center gap-2"
                style={{ backgroundColor: themes[themeName].colors.primary, color: themes[themeName].colors.surface }}
              >
                <FaPlus size={16} />
                Add New Campaign
              </button>
            ) : (
              <div style={{ backgroundColor: themes[themeName].colors.warning, color: themes[themeName].colors.surface, padding: '0.5rem 1rem', borderRadius: 8 }}>
                <span>Complete profile verification to create campaigns</span>
              </div>
            )}
          </>
        )}
      </div>

      {filteredCampaigns?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns?.map((campaign: any) => (
            <div
              key={campaign.id}
              className="rounded-xl shadow-sm hover:shadow-md transition-all duration-300 relative group border"
              style={{ backgroundColor: themes[themeName].colors.card, borderColor: themes[themeName].colors.border }}
            >
              {/* Campaign Image */}
              <div className="h-40 overflow-hidden relative">
                <div className="absolute top-2 left-2 z-10">
                  {campaign?.is_live ?
                    <span className="px-2 py-1 rounded-full text-xs font-semibold backdrop-blur-md" style={{ backgroundColor: 'rgba(16, 185, 129, 0.9)', color: '#fff' }}>Live</span>
                    :
                    <span className="px-2 py-1 rounded-full text-xs font-semibold backdrop-blur-md" style={{ backgroundColor: 'rgba(245, 158, 11, 0.9)', color: '#fff' }}>Draft</span>
                  }
                </div>

                <div className="absolute top-2 right-2 z-10" onClick={() => setOpenMenuId(openMenuId === campaign.id ? null : campaign.id)}>
                  <button className="p-1.5 rounded-full backdrop-blur-md hover:bg-black/10 transition-colors text-white">
                    <HiOutlineDotsVertical size={20} />
                  </button>
                  {
                    openMenuId === campaign.id &&
                    <div className="absolute right-0 mt-2">
                      <CampaignMenu
                        campaign={campaign}
                        setOpenMenuId={setOpenMenuId}
                        openMenuId={openMenuId}
                        refreshCampaigns={refetchCampaigns}
                        onEdit={handleEditCampaign}
                        onDelete={handleDeleteClick}
                      />
                    </div>
                  }
                </div>

                <img
                  src={campaign?.banner}
                  alt="Campaign Banner"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                <div className="absolute bottom-3 left-3 text-white">
                  <h3 className="text-lg font-bold leading-tight drop-shadow-sm">
                    {campaign.title}
                  </h3>
                  <p className="text-xs opacity-90 flex items-center gap-1 mt-1">
                    <FaBuilding size={10} /> {campaign?.brand_title}
                  </p>
                </div>
              </div>

              {/* Campaign Content */}
              <div className="p-4 flex flex-col h-[calc(100%-160px)]">
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-opacity-10 flex items-center gap-1.5" style={{ backgroundColor: themes[themeName].colors.primary + '15', color: themes[themeName].colors.primary }}>
                    <FaTag size={10} />
                    {campaign?.type}
                  </span>
                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-opacity-10 flex items-center gap-1.5" style={{ backgroundColor: themes[themeName].colors.text + '10', color: themes[themeName].colors.textSecondary }}>
                    <FaCalendar size={10} />
                    {new Date(campaign?.deadline).toLocaleDateString()}
                  </span>
                </div>

                {/* Actions Grid */}
                <div className="mt-auto grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleViewCampaign(campaign.id)}
                    className="flex justify-center items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border"
                    style={{
                      borderColor: themes[themeName].colors.border,
                      color: themes[themeName].colors.text
                    }}
                  >
                    <FaEye size={14} />
                    View
                  </button>

                  <button
                    onClick={() => handleCreateOrder(campaign)}
                    className="flex justify-center items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: themes[themeName].colors.success + '15',
                      color: themes[themeName].colors.success
                    }}
                  >
                    <FaMoneyBillWave size={14} />
                    Pay
                  </button>

                  <button
                    onClick={() => handleShowInfluencers(campaign)}
                    className="flex justify-center items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border"
                    style={{
                      borderColor: themes[themeName].colors.border,
                      color: themes[themeName].colors.textSecondary
                    }}
                  >
                    <FaUsers size={14} />
                    Influencers
                  </button>

                  <button
                    onClick={() => handleShowRequests(campaign)}
                    className="flex justify-center items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border"
                    style={{
                      borderColor: themes[themeName].colors.border,
                      color: themes[themeName].colors.textSecondary
                    }}
                  >
                    <FiCloudLightning size={14} />
                    Requests
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center mt-10" style={{ color: themes[themeName].colors.textMuted }}>No campaigns found.</p>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && campaignToDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-[110] p-4" style={{ backgroundColor: themes[themeName].colors.background + 'CC' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
            <div className="p-6 text-center" style={{ backgroundColor: themes[themeName].colors.surface, borderRadius: '1rem' }}>
              {/* Warning Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4" style={{ backgroundColor: themes[themeName].colors.error + '20' }}>
                <FaExclamationTriangle className="h-8 w-8" style={{ color: themes[themeName].colors.error }} />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold mb-2" style={{ color: themes[themeName].colors.text }}>
                Delete Campaign
              </h3>

              {/* Message */}
              <div className="mb-6">
                <p className="text-sm" style={{ color: themes[themeName].colors.textSecondary }}>
                  Are you sure you want to delete the campaign
                </p>
                <p className="font-semibold mt-1" style={{ color: themes[themeName].colors.text }}>
                  "{campaignToDelete.title}"?
                </p>
                <p className="text-xs mt-2" style={{ color: themes[themeName].colors.error }}>
                  This action cannot be undone.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleCancelDelete}
                  className="px-6 py-2.5 rounded-lg font-medium transition-all duration-200 border"
                  style={{
                    color: themes[themeName].colors.textSecondary,
                    backgroundColor: themes[themeName].colors.surface,
                    borderColor: themes[themeName].colors.border
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.background;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.surface;
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-6 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  style={{
                    backgroundColor: themes[themeName].colors.error,
                    color: themes[themeName].colors.surface
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.error + 'DD';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = themes[themeName].colors.error;
                  }}
                >
                  <MdDelete size={16} />
                  Delete Campaign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for New/Edit Campaign */}
      {isModalOpen && (
        <div className="fixed z-[100] inset-0 flex items-center justify-center" style={{ backgroundColor: themes[themeName].colors.background + 'CC' }}>
          <div className="rounded-lg shadow-2xl w-[97%] md:w-[70%] max-h-[85vh] overflow-y-auto" style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text }}>
            {/* Modal Header */}
            <div className="px-6 py-4 border-b" style={{ background: `linear-gradient(to right, ${themes[themeName].colors.primary}, ${themes[themeName].colors.secondary})`, borderBottom: `1px solid ${themes[themeName].colors.border}`, color: themes[themeName].colors.surface }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: themes[themeName].colors.card }}>
                    <FaPlus style={{ color: themes[themeName].colors.primary }} size={20} />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {editingCampaign ? "Edit Campaign" : "Create New Campaign"}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingCampaign(null);
                    resetForm();
                  }}
                  className="transition-colors"
                  style={{ color: themes[themeName].colors.surface }}
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Title and Overview */}
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Campaign Title {errors.campaignName && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <input
                      type="text"
                      placeholder="Enter campaign title"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      className={`border p-2 rounded-md w-full focus:ring-2 ${errors.campaignName ? "border-red-500 focus:ring-red-500" : "focus:ring-primary-500"
                        }`}
                      style={{
                        backgroundColor: errors.campaignName ? themes[themeName].colors.error : themes[themeName].colors.surface,
                        color: errors.campaignName ? themes[themeName].colors.surface : themes[themeName].colors.text,
                        borderColor: errors.campaignName ? themes[themeName].colors.error : themes[themeName].colors.border
                      }}
                    />
                    {errors.campaignName && (
                      <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.campaignName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Campaign Overview {errors.campaingOverview && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <textarea
                      placeholder="Enter campaign overview"
                      value={campaingOverview}
                      onChange={(e) => setCampaingOverview(e.target.value)}
                      rows={3}
                      className={`border p-2 rounded-md w-full focus:ring-2 resize-none ${errors.campaingOverview ? "border-red-500" : ""}`}
                      style={{
                        backgroundColor: errors.campaingOverview ? themes[themeName].colors.error : themes[themeName].colors.surface,
                        color: errors.campaingOverview ? themes[themeName].colors.surface : themes[themeName].colors.text,
                        borderColor: errors.campaingOverview ? themes[themeName].colors.error : themes[themeName].colors.border,
                      }}
                    />
                    {errors.campaingOverview && (
                      <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.campaingOverview}</p>
                    )}
                  </div>
                </div>

                {/* Brand, Type and Deadline Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Select Brand {errors.selectedBrandId && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <select
                      value={selectedBrandId}
                      onChange={(e) => setSelectedBrandId(e.target.value)}
                      className={`border p-2 rounded-md w-full focus:ring-2`}
                      style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: errors.selectedBrandId ? themes[themeName].colors.error : themes[themeName].colors.border }}
                    >
                      <option value="">Choose a brand</option>
                      {brands?.map((brand) => (
                        <option key={brand.id} value={brand.id}>
                          {brand.title}
                        </option>
                      ))}
                    </select>
                    {errors.selectedBrandId && (
                      <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.selectedBrandId}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Campaign Type {errors.type && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <div className="relative" ref={typeDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setTypeDropdownOpen((open) => !open)}
                        className={`w-full border p-2 rounded-md flex justify-between items-center`}
                        style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: errors.type ? themes[themeName].colors.error : themes[themeName].colors.border }}
                      >
                        <span>
                          {!type ? "Select campaign type" : type}
                        </span>
                        <svg
                          className={`w-4 h-4 ml-2 transition-transform ${typeDropdownOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {errors.type && (
                        <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.type}</p>
                      )}
                      {typeDropdownOpen && (
                        <div className="absolute z-10 mt-2 w-full rounded-md shadow-lg p-2 max-h-60 overflow-y-auto" style={{ backgroundColor: themes[themeName].colors.surface, border: `1px solid ${themes[themeName].colors.border}` }}>
                          {[
                            "PHOTOS",
                            "VIDEOS",
                            "LEARNING",
                            "LIFESTYLE",
                            "HEALTH",
                            "SPORTS",
                            "DANCE",
                            "OTHERS",
                          ].map((option) => (
                            <label key={option} className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer" style={{ backgroundColor: 'transparent' }}>
                              <input
                                type="radio"
                                name="campaign-type"
                                value={option}
                                checked={type === option}
                                onChange={() => setType(option)}
                                className="focus:ring-primary-500"
                                style={{ accentColor: themes[themeName].colors.primary }}
                              />
                              <span>{option}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Deadline {errors.deadline && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <input
                      type="date"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                      className={`border p-2 rounded-md w-full focus:ring-2 ${errors.deadline ? "border-red-500 focus:ring-red-500" : "focus:ring-primary-500"
                        }`}
                      style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: errors.deadline ? themes[themeName].colors.error : themes[themeName].colors.border }}
                    />
                    {errors.deadline && (
                      <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.deadline}</p>
                    )}
                  </div>
                </div>

                {/* Platforms Section */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                    Campaign Platforms {errors.platforms && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                  </label>
                  {errors.platforms && (
                    <p className="text-sm mb-2" style={{ color: themes[themeName].colors.error }}>{errors.platforms}</p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availablePlatforms.map((platform) => (
                      <label
                        key={platform}
                        className="flex items-center gap-2 p-2 rounded cursor-pointer"
                        style={{ border: `1px solid ${themes[themeName].colors.border}` }}
                      >
                        <input
                          type="checkbox"
                          checked={platforms.includes(platform)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPlatforms([...platforms, platform]);
                            } else {
                              setPlatforms(
                                platforms.filter((p) => p !== platform)
                              );
                            }
                          }}
                          className="focus:ring-primary-500"
                          style={{ accentColor: themes[themeName].colors.primary }}
                        />
                        <span>{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Budget Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Campaign Budget {errors.campaignBudget && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2" style={{ color: themes[themeName].colors.textMuted }}>₹</span>
                      <input
                        type="number"
                        placeholder="Enter campaign budget"
                        value={campaignBudget}
                        onChange={(e) => setCampaignBudget(e.target.value)}
                        className={`border p-2 pl-7 rounded-md w-full focus:ring-2 ${errors.campaignBudget ? "border-red-500 focus:ring-red-500" : "focus:ring-primary-500"
                          }`}
                        style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: errors.campaignBudget ? themes[themeName].colors.error : themes[themeName].colors.border }}
                      />
                    </div>
                    {errors.campaignBudget && (
                      <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.campaignBudget}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                      Individual Budget {errors.individualBudget && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2" style={{ color: themes[themeName].colors.textMuted }}>₹</span>
                      <input
                        type="number"
                        placeholder="Enter individual budget"
                        value={individualBudget}
                        onChange={(e) => setIndividualBudget(e.target.value)}
                        className={`border p-2 pl-7 rounded-md w-full focus:ring-2 ${errors.individualBudget ? "border-red-500 focus:ring-red-500" : "focus:ring-primary-500"
                          }`}
                        style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text, borderColor: errors.individualBudget ? themes[themeName].colors.error : themes[themeName].colors.border }}
                      />
                    </div>
                    {errors.individualBudget && (
                      <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.individualBudget}</p>
                    )}
                  </div>
                </div>

                {/* Banner Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: themes[themeName].colors.textSecondary }}>
                    Campaign Banner
                    {!editingCampaign && errors.bannerImage && <span style={{ color: themes[themeName].colors.error }}>*</span>}
                    {editingCampaign && (
                      <span className="text-xs ml-2" style={{ color: themes[themeName].colors.textMuted }}>
                        (Optional - Upload new image to replace current)
                      </span>
                    )}
                  </label>

                  {/* Show current image for editing */}
                  {editingCampaign && editingCampaign.banner && !bannerPreview && (
                    <div className="mb-4">
                      <p className="text-sm mb-2" style={{ color: themes[themeName].colors.textSecondary }}>
                        Current Banner:
                      </p>
                      <img
                        src={editingCampaign.banner}
                        alt="Current banner"
                        className="h-32 w-full object-cover rounded-md"
                        style={{ border: `1px solid ${themes[themeName].colors.border}` }}
                      />
                    </div>
                  )}

                  {/* Upload area */}
                  <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${!editingCampaign && errors.bannerImage ? "border-red-500" : "border-gray-300 hover:border-primary-500"
                    }`}>
                    <div className="space-y-1 text-center">
                      <div className="flex text-sm" style={{ color: themes[themeName].colors.textSecondary }}>
                        <label className="relative cursor-pointer rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2" style={{ color: themes[themeName].colors.primary }}>
                          <span>Upload a file</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setBannerImage(file);
                                setBannerPreview(URL.createObjectURL(file));
                                // Clear error if file is selected
                                if (errors.bannerImage) {
                                  setErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.bannerImage;
                                    return newErrors;
                                  });
                                }
                              }
                            }}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs" style={{ color: themes[themeName].colors.textMuted }}>
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>
                  </div>

                  {/* Show error only for new campaigns */}
                  {!editingCampaign && errors.bannerImage && (
                    <p className="text-sm mt-1" style={{ color: themes[themeName].colors.error }}>{errors.bannerImage}</p>
                  )}

                  {/* Show new image preview */}
                  {bannerPreview && (
                    <div className="mt-4">
                      <p className="text-sm mb-2" style={{ color: themes[themeName].colors.textSecondary }}>
                        New Banner Preview:
                      </p>
                      <img
                        src={bannerPreview}
                        alt="New banner preview"
                        className="h-32 w-full object-cover rounded-md"
                        style={{ border: `1px solid ${themes[themeName].colors.border}` }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setBannerImage(null);
                          setBannerPreview(null);
                        }}
                        className="mt-2 text-sm flex items-center gap-1"
                        style={{ color: themes[themeName].colors.error }}
                      >
                        <FaTimes size={12} />
                        Remove new image
                      </button>
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                {progress > 0 && (
                  <div className="w-full rounded-full h-2" style={{ backgroundColor: themes[themeName].colors.border }}>
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%`, backgroundColor: themes[themeName].colors.primary }}
                    ></div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ backgroundColor: themes[themeName].colors.background, borderTop: `1px solid ${themes[themeName].colors.border}` }}>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingCampaign(null);
                  resetForm();
                }}
                className="px-4 py-2 rounded-md transition-colors"
                style={{ color: themes[themeName].colors.textSecondary, backgroundColor: themes[themeName].colors.surface, border: `1px solid ${themes[themeName].colors.border}` }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-md flex items-center gap-2"
                style={{ backgroundColor: themes[themeName].colors.primary, color: themes[themeName].colors.surface }}
              >
                <FaPlus size={16} />
                {editingCampaign ? "Update Campaign" : "Create Campaign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Campaign Detail Modal */}
      {isDetailModalOpen && selectedCampaign && (
        <div className="fixed z-[101] inset-0 flex items-center justify-center" style={{ backgroundColor: themes[themeName].colors.background + 'CC' }}>
          <div className="rounded-lg shadow-xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text }}>
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b sticky top-0 z-10" style={{ backgroundColor: themes[themeName].colors.surface, borderBottom: `1px solid ${themes[themeName].colors.border}` }}>
              <h3 className="text-xl font-semibold" style={{ color: themes[themeName].colors.primary }}>{selectedCampaign.title}</h3>
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="transition-colors"
                style={{ color: themes[themeName].colors.textSecondary }}
              >
                <FaTimes size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Campaign Banner */}
              <div className="mb-6">
                <img
                  src={selectedCampaign.banner}
                  alt="Campaign Banner"
                  className="w-full h-64 object-cover rounded-lg"
                  style={{ border: `1px solid ${themes[themeName].colors.border}` }}
                />
              </div>

              {/* Campaign Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <FaBuilding style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>Brand: {selectedCampaign?.brand_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaTag style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>Type: {selectedCampaign?.type} </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaCalendar style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>
                      Deadline: {new Date(selectedCampaign?.deadline).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiCloudLightning style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>
                      Platforms: {
                        selectedCampaign?.platforms
                          ? (() => {
                            const p = selectedCampaign.platforms;
                            const arr = Array.isArray(p) ? p as string[] : JSON.parse(String(p)) as string[];
                            return Array.isArray(arr) ? arr.join(", ") : String(p);
                          })()
                          : "Not specified"
                      }
                    </span>
                  </div>
                </div>

                {/* Budget Information */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <FaMoneyBillWave style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>Campaign Budget: ₹{selectedCampaign?.campaign_budget}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaUser style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>Individual Budget: ₹{selectedCampaign?.individual_budget}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaWallet style={{ color: themes[themeName].colors.primary }} />
                    <span style={{ color: themes[themeName].colors.textSecondary }}>Budget Left: ₹{selectedCampaign?.budget_left || "NOT DEFINED"}</span>
                  </div>
                </div>

                {/* Show Requests Button */}
                {userType !== 'ADMIN' && (
                  <div className="flex justify-end items-start">
                    <button onClick={() => setShowInfluencer(true)}
                      className="px-4 py-2 rounded flex items-center gap-2"
                      style={{ backgroundColor: themes[themeName].colors.primary, color: themes[themeName].colors.surface }}
                    >
                      <FaUsers /> Influencers List
                    </button>
                  </div>
                )}
              </div>

              {
                showInfluencer &&
                <InfluencerModal showInfluencer={showInfluencer} setShowInfluencer={setShowInfluencer} campaign={selectedCampaign} />
              }

              {/* Rules Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold" style={{ color: themes[themeName].colors.primary }}>Rules</h3>
                  {userType !== "ADMIN" && (
                    <div className="flex gap-2">
                      {rules && (
                        <button
                          onClick={() => handleEditRules(rules)}
                          className="px-4 py-2 rounded flex items-center gap-2"
                          style={{ backgroundColor: themes[themeName].colors.secondary, color: themes[themeName].colors.surface }}
                        >
                          <FaEdit /> Edit Rules
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setIsEditRulesMode(false);
                          setEditingRules(null);
                          setIsRuleModalOpen(true);
                        }}
                        className="px-4 py-2 rounded flex items-center gap-2"
                        style={{ backgroundColor: themes[themeName].colors.primary, color: themes[themeName].colors.surface }}
                      >
                        <FaPlus /> Add Rules
                      </button>
                    </div>
                  )}
                </div>

                {rules ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Do's Section */}
                    <div className="relative">
                      <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.primary }}>Do's:</h4>
                      {rules.dos && rules.dos.length > 0 ? (
                        <ul className="list-disc list-inside" style={{ color: themes[themeName].colors.textSecondary }}>
                          {(rules.dos as string[])
                            .filter((rule: string) => rule.trim() !== '')
                            .map((rule: string, index: number) => (
                              <li key={index} className="p-2" style={{ borderBottom: `1px solid ${themes[themeName].colors.border}` }}>
                                {rule}
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <p style={{ color: themes[themeName].colors.textMuted }}>No do's added yet.</p>
                      )}
                    </div>

                    {/* Don'ts Section */}
                    <div className="relative">
                      <h4 className="font-semibold mb-2" style={{ color: themes[themeName].colors.primary }}>Don'ts:</h4>
                      {rules.donts && rules.donts.length > 0 ? (
                        <ul className="list-disc list-inside" style={{ color: themes[themeName].colors.textSecondary }}>
                          {(rules.donts as string[])
                            .filter((rule: string) => rule.trim() !== '')
                            .map((rule: string, index: number) => (
                              <li key={index} className="p-2" style={{ borderBottom: `1px solid ${themes[themeName].colors.border}` }}>
                                {rule}
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <p style={{ color: themes[themeName].colors.textMuted }}>No don'ts added yet.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8" style={{ color: themes[themeName].colors.textMuted }}>
                    <p>No rules added yet. Click "Add Rules" to get started.</p>
                  </div>
                )}
              </div>

              {/* Tasks Section */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4" style={{ color: themes[themeName].colors.primary }}>Tasks</h3>
                <Tasks id={String(selectedCampaign.id)} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rules Modal */}
      {isRuleModalOpen && selectedCampaign && (
        <RulesModal
          isOpen={isRuleModalOpen}
          onClose={isEditRulesMode ? handleCancelEditRules : () => setIsRuleModalOpen(false)}
          onSave={async (dos, donts) => {
            if (selectedCampaign) {
              const formData = new FormData();
              formData.append('dos', JSON.stringify(dos));
              formData.append('donts', JSON.stringify(donts));
              formData.append('campaign', selectedCampaign.id.toString());

              try {
                const response = await postSingleCampaignRules(formData);
                if (response && response.status === 201) {
                  toast.success("Rules added successfully!");
                  loadRules(selectedCampaign.id);
                  setIsRuleModalOpen(false);
                  setToastMessage("Rules added successfully!");
                  setToastType("success");
                  setIsToastVisible(true);
                }
              } catch (err) {
                console.log(err);
                setToastMessage("An error occurred while adding rules. Please try again.");
                setToastType("error");
                setIsToastVisible(true);
              }
            }
          }}
          onUpdate={isEditRulesMode ? handleUpdateRules : undefined}
          theme={themes[themeName].colors}
          campaignId={selectedCampaign.id.toString()}
          existingRules={isEditRulesMode ? editingRules : null}
          isEditMode={isEditRulesMode}
        />
      )}

      {/* Enhanced Requests Modal */}
      {isRequestModalOpen && selectedCampaignForRequests && (
        <div className="fixed inset-0 flex items-center justify-center z-[101]" style={{ backgroundColor: themes[themeName].colors.background + 'CC' }}>
          <div className="w-[80%] max-h-[85vh] overflow-y-auto rounded-lg shadow-lg" style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text }}>
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b" style={{ background: `linear-gradient(to right, ${themes[themeName].colors.primary}, ${themes[themeName].colors.secondary})`, borderBottom: `1px solid ${themes[themeName].colors.border}`, color: themes[themeName].colors.surface }}>
              <h2 className="text-xl font-bold" style={{ color: themes[themeName].colors.surface }}>
                Requests for {selectedCampaignForRequests.title}
              </h2>
              <button
                onClick={() => setIsRequestModalOpen(false)}
                className="transition-colors"
                style={{ color: themes[themeName].colors.surface }}
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {loadingRequests ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: themes[themeName].colors.primary }}></div>
                </div>
              ) : requests?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {requests?.map((request: any) => (
                    <div
                      key={request.id}
                      className="rounded-lg p-4 transition-shadow"
                      style={{ border: `1px solid ${themes[themeName].colors.border}`, backgroundColor: themes[themeName].colors.card }}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className='cursor-pointer flex-1' onClick={() => navigate(`/dashboard/user/profile/${request?.user}`)}>
                          <h4 className="font-semibold text-lg" style={{ color: themes[themeName].colors.text }}>
                            {request?.user_name}
                          </h4>
                          <div className="text-sm mt-2" style={{ color: themes[themeName].colors.textSecondary }}>
                            <p>
                              <strong>Requested on:</strong>{" "}
                              {new Date(request.created_at).toLocaleDateString()}
                            </p>
                            <p>
                              <strong>Email:</strong> {request?.user_email || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <span
                          className="px-3 py-1 rounded-full text-sm cursor-pointer"
                          onClick={() => handleStatusClick(request)}
                          style={{
                            backgroundColor: request.status === "APPROVED" ? themes[themeName].colors.success :
                              request.status === "REJECTED" ? themes[themeName].colors.error :
                                themes[themeName].colors.warning,
                            color: themes[themeName].colors.surface
                          }}
                        >
                          {request.status}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderTop: `1px solid ${themes[themeName].colors.border}` }}>
                        <button
                          onClick={() => navigate(`/dashboard/user/profile/${request?.user}`)}
                          className="flex items-center gap-1 text-sm"
                          style={{ color: themes[themeName].colors.primary }}
                        >
                          <FaEye size={14} />
                          View Profile
                        </button>

                        <button
                          onClick={() => handleStatusClick(request)}
                          className="flex items-center gap-1 text-sm"
                          style={{ color: themes[themeName].colors.primary }}
                        >
                          <FaEdit size={14} />
                          Manage Request
                        </button>

                        <button
                          onClick={() => updateRequestStatus(request, "APPROVED")}
                          className="flex items-center gap-1 text-sm"
                          style={{ color: themes[themeName].colors.success }}
                        >
                          <FaCheck size={14} />
                          Approve
                        </button>

                        <button
                          onClick={() => updateRequestStatus(request, "REJECTED")}
                          className="flex items-center gap-1 text-sm"
                          style={{ color: themes[themeName].colors.error }}
                        >
                          <FaBan size={14} />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaUsers className="mx-auto text-4xl mb-4" style={{ color: themes[themeName].colors.textMuted }} />
                  <h3 className="text-lg font-semibold mb-2" style={{ color: themes[themeName].colors.textMuted }}>
                    No requests yet
                  </h3>
                  <p style={{ color: themes[themeName].colors.textMuted }}>
                    No influencers have applied to this campaign yet.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t flex justify-end" style={{ borderTop: `1px solid ${themes[themeName].colors.border}`, backgroundColor: themes[themeName].colors.surface }}>
              <button
                onClick={() => setIsRequestModalOpen(false)}
                className="px-4 py-2 rounded"
                style={{ backgroundColor: themes[themeName].colors.primary, color: themes[themeName].colors.surface }}
              >
                Close
              </button>
            </div>
          </div>

          {/* Status Update Popup */}
          {isStatusPopupOpen && selectedRequest && (
            <div className="fixed inset-0 z-[102] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
              <div
                className="rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
                style={{
                  backgroundColor: themes[themeName].colors.surface,
                  borderColor: themes[themeName].colors.border,
                  borderWidth: 1,
                  borderStyle: 'solid'
                }}
              >
                {/* Header */}
                <div className="text-center mb-6">
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: themes[themeName].colors.text }}
                  >
                    Update Request Status
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: themes[themeName].colors.textSecondary }}
                  >
                    Choose the status for {selectedRequest?.user_name}'s request
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mb-6">
                  <button
                    onClick={() => updateRequestStatus(selectedRequest, "APPROVED")}
                    className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{
                      backgroundColor: themes[themeName].colors.success,
                      color: themes[themeName].colors.surface,
                    }}
                  >
                    <FaCheck />
                    Approve Request
                  </button>

                  <button
                    onClick={() => updateRequestStatus(selectedRequest, "REJECTED")}
                    className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{
                      backgroundColor: themes[themeName].colors.error,
                      color: themes[themeName].colors.surface,
                    }}
                  >
                    <FaBan />
                    Reject Request
                  </button>
                </div>

                {/* Cancel Button */}
                <div className="text-center">
                  <button
                    onClick={() => setIsStatusPopupOpen(false)}
                    className="px-4 py-2 rounded-lg font-medium transition-colors"
                    style={{
                      color: themes[themeName].colors.textSecondary,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Influencer Modal */}
      {showInfluencer && (
        <InfluencerModal
          showInfluencer={showInfluencer}
          setShowInfluencer={setShowInfluencer}
          campaign={selectedCampaign}
        />
      )}

      {/* Toast Component */}
      <Toast
        message={toastMessage}
        type={toastType}
        show={isToastVisible}
        onClose={() => setIsToastVisible(false)}
        duration={3000}
      />
    </div>
  );
};

export default Campaigns;